/**
 * @component media-sources-settings
 * @css ../css/components/media-sources-settings.css
 * @primitives .btn, .btn-primary, .btn-secondary, .loading-state, .spinner, .form-group, .form-label, .form-control, .form-text
 */
// Media Sources Settings Web Component
import { escapeHtml } from '../utils/html-utils.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class MediaSourcesSettings extends HTMLElement {
  constructor() {
    super();
    this.accounts = {};
    this.availableProviders = [];
    this.isLoading = true;
    this.errorMessage = null;
    this.currentUser = null;
    this.connectModalProvider = null;
    this.connectError = null;
    this.connectSubmitting = false;
  }

  connectedCallback() {
    this.render();
    // Wait for setCurrentUser to be called before loading accounts
  }

  setCurrentUser(user) {
    this.currentUser = user;
    if (this.currentUser) {
      this.loadAccounts().then(() => {
        this.attachEventListeners();
      });
    }
  }

  async loadAccounts() {
    if (!this.currentUser || !this.currentUser.id) {
      this.errorMessage = 'User not authenticated';
      this.isLoading = false;
      this.render();
      return;
    }

    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      const data = await window.API.request(`/users/settings/${this.currentUser.id}/media-sources`);
      this.accounts = data.accounts || {};
      this.availableProviders = data.availableProviders || [];
    } catch (error) {
      window.logger.error('Failed to load media source accounts:', error);
      this.errorMessage = 'Failed to load media source accounts';
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  async disconnectAccount(accountId) {
    if (!confirm('Are you sure you want to disconnect this account?')) {
      return;
    }

    try {
      await window.API.request(`/users/settings/${this.currentUser.id}/media-sources/${accountId}`, {
        method: 'DELETE'
      });

      // Reload accounts
      await this.loadAccounts();
    } catch (error) {
      window.logger.error('Failed to disconnect account:', error);
      alert('Failed to disconnect account. Please try again.');
    }
  }

  connectProvider(providerName) {
    const provider = this.availableProviders.find(p => p.name === providerName);
    if (provider && (provider.connectMode === 'form' || provider.authType === 'api-key')) {
      this.connectModalProvider = provider;
      this.connectError = null;
      this.connectSubmitting = false;
      this.render();
      this.attachEventListeners();
      return;
    }
    window.location.href = `/api/media-sources/authorize/${providerName}`;
  }

  closeConnectModal() {
    this.connectModalProvider = null;
    this.connectError = null;
    this.connectSubmitting = false;
    this.render();
    this.attachEventListeners();
  }

  async submitImmichConnect(form) {
    if (!this.currentUser || this.connectSubmitting) {
      return;
    }

    const baseUrl = form.querySelector('#immich-base-url')?.value?.trim();
    const apiKey = form.querySelector('#immich-api-key')?.value?.trim();
    const accountLabel = form.querySelector('#immich-account-label')?.value?.trim();

    if (!baseUrl || !apiKey) {
      this.connectError = 'Immich URL and API key are required';
      this.render();
      this.attachEventListeners();
      return;
    }

    this.connectSubmitting = true;
    this.connectError = null;
    this.render();
    this.attachEventListeners();

    try {
      const body = { baseUrl, apiKey };
      if (accountLabel) {
        body.accountLabel = accountLabel;
      }

      await window.API.request('/media-sources/connect/immich', {
        method: 'POST',
        body: JSON.stringify(body)
      });

      this.connectModalProvider = null;
      this.connectSubmitting = false;
      await this.loadAccounts();
      this.attachEventListeners();
    } catch (error) {
      window.logger.error('Failed to connect Immich:', error);
      this.connectSubmitting = false;
      this.connectError = error?.message || error?.error || 'Failed to connect Immich. Check the URL and API key.';
      this.render();
      this.attachEventListeners();
    }
  }

  render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div class="media-sources-settings">
          <div class="loading-state">
            <div class="spinner"></div>
            <div>Loading media sources...</div>
          </div>
        </div>
      `;
      return;
    }

    if (this.errorMessage) {
      this.innerHTML = `
        <div class="media-sources-settings">
          <div class="error-state">
            <p>${escapeHtml(this.errorMessage)}</p>
            <button class="btn btn-primary retry-btn">Retry</button>
          </div>
        </div>
      `;
      // Attach retry button listener
      const retryBtn = this.querySelector('.retry-btn');
      if (retryBtn) {
        retryBtn.addEventListener('click', () => {
          this.errorMessage = null;
          this.isLoading = true;
          this.render();
          this.loadAccounts();
        });
      }
      return;
    }

    this.innerHTML = `
      <div class="media-sources-settings">
        <div class="settings-header">
          ${renderPageTitle('✨', 'Sparkle Sources')}
          <p>Connect your cloud storage accounts to embed files in your posts</p>
        </div>

        <div class="settings-content">
          ${this.renderAvailableProviders()}
          ${this.renderConnectedAccounts()}
        </div>

        <div class="settings-footer">
          <button class="btn btn-secondary" id="returnToSettingsBtn">
            ← Return to Settings
          </button>
        </div>

        ${this.renderConnectModal()}
      </div>
    `;
  }

  renderConnectModal() {
    if (!this.connectModalProvider) {
      return '';
    }

    const provider = this.connectModalProvider;
    const isImmich = provider.name === 'immich';

    if (!isImmich) {
      return '';
    }

    return `
      <div class="connect-modal-backdrop" data-connect-modal>
        <div class="connect-modal card" role="dialog" aria-labelledby="immich-connect-title">
          <h3 id="immich-connect-title">Connect Immich</h3>
          <p class="connect-modal-help">
            Use a publicly reachable HTTPS Immich URL and an API key from Immich
            (Account Settings → API Keys) with album and asset read permissions.
          </p>
          ${this.connectError ? `<p class="connect-modal-error">${escapeHtml(this.connectError)}</p>` : ''}
          <form id="immich-connect-form">
            <div class="form-group">
              <label class="form-label" for="immich-base-url">Immich URL</label>
              <input class="form-control" type="url" id="immich-base-url" name="baseUrl"
                placeholder="https://photos.example.com" required autocomplete="off"
                ${this.connectSubmitting ? 'disabled' : ''} />
              <p class="form-text">Must be public HTTPS (not localhost or a private LAN address).</p>
            </div>
            <div class="form-group">
              <label class="form-label" for="immich-api-key">API key</label>
              <input class="form-control" type="password" id="immich-api-key" name="apiKey"
                required autocomplete="off" ${this.connectSubmitting ? 'disabled' : ''} />
            </div>
            <div class="form-group">
              <label class="form-label" for="immich-account-label">Label (optional)</label>
              <input class="form-control" type="text" id="immich-account-label" name="accountLabel"
                placeholder="Home Immich" autocomplete="off"
                ${this.connectSubmitting ? 'disabled' : ''} />
            </div>
            <div class="connect-modal-actions">
              <button type="button" class="btn btn-secondary" id="immich-connect-cancel"
                ${this.connectSubmitting ? 'disabled' : ''}>Cancel</button>
              <button type="submit" class="btn btn-primary" ${this.connectSubmitting ? 'disabled' : ''}>
                ${this.connectSubmitting ? 'Connecting…' : 'Connect'}
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  renderAvailableProviders() {
    if (this.availableProviders.length === 0) {
      return `
        <div class="providers-section">
          <h3>Available Providers</h3>
          <p class="no-providers">No providers are currently enabled.</p>
        </div>
      `;
    }

    return `
      <div class="providers-section">
        <h3>Available Providers</h3>
        <div class="providers-grid">
          ${this.availableProviders.map(provider => `
            <div class="provider-card">
              <div class="provider-header">
                <h4>${escapeHtml(provider.displayName)}</h4>
                ${provider.pricingInfo?.hasFreeTier ? '<span class="badge badge-free">Free Tier Available</span>' : ''}
              </div>
              <p class="provider-description">${escapeHtml(provider.description || '')}</p>
              <div class="provider-actions">
                <button class="btn btn-primary connect-btn" data-provider="${escapeHtml(provider.name)}">
                  Connect ${escapeHtml(provider.displayName)}
                </button>
                ${provider.learnMoreUrl ? `<a href="${escapeHtml(provider.learnMoreUrl)}" target="_blank" class="btn btn-secondary">Learn More</a>` : ''}
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  renderConnectedAccounts() {
    const accountCount = Object.values(this.accounts).reduce((sum, accounts) => sum + accounts.length, 0);

    if (accountCount === 0) {
      return `
        <div class="accounts-section">
          <h3>Connected Accounts</h3>
          <p class="no-accounts">No accounts connected yet. Connect a provider above to get started.</p>
        </div>
      `;
    }

    return `
      <div class="accounts-section">
        <h3>Connected Accounts</h3>
        ${Object.entries(this.accounts).map(([provider, accounts]) => `
          <div class="provider-accounts">
            <h4>${this.getProviderDisplayName(provider)}</h4>
            <div class="accounts-list">
              ${accounts.map(account => `
                <div class="account-item ${account.isExpired ? 'expired' : ''}">
                  <div class="account-info">
                    <div class="account-identifier">
                      <strong>${escapeHtml(account.accountIdentifier || 'Unknown')}</strong>
                      ${account.accountLabel ? `<span class="account-label">${escapeHtml(account.accountLabel)}</span>` : ''}
                    </div>
                    <div class="account-meta">
                      ${account.isExpired ? '<span class="badge badge-warning">Expired</span>' : '<span class="badge badge-success">Active</span>'}
                      ${account.lastUsedAt ? `<span class="last-used">Last used: ${this.formatDate(account.lastUsedAt)}</span>` : ''}
                      ${account.expiresAt ? `<span class="expires-at">Expires: ${this.formatDate(account.expiresAt)}</span>` : ''}
                    </div>
                  </div>
                  <button class="btn btn-danger disconnect-btn" data-account-id="${account.id}">
                    Disconnect
                  </button>
                </div>
              `).join('')}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  getProviderDisplayName(providerName) {
    const provider = this.availableProviders.find(p => p.name === providerName);
    return provider ? provider.displayName : providerName;
  }

  formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  attachEventListeners() {
    // Connect provider buttons
    this.querySelectorAll('.connect-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const provider = e.currentTarget.dataset.provider;
        this.connectProvider(provider);
      });
    });

    // Disconnect account buttons
    this.querySelectorAll('.disconnect-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const accountId = parseInt(e.currentTarget.dataset.accountId, 10);
        this.disconnectAccount(accountId);
      });
    });

    // Return to settings button
    const returnBtn = this.querySelector('#returnToSettingsBtn');
    if (returnBtn) {
      returnBtn.addEventListener('click', () => {
        if (window.router) {
          window.router.navigate('/settings');
        }
      });
    }

    const cancelBtn = this.querySelector('#immich-connect-cancel');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.closeConnectModal());
    }

    const backdrop = this.querySelector('[data-connect-modal]');
    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) {
          this.closeConnectModal();
        }
      });
    }

    const form = this.querySelector('#immich-connect-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitImmichConnect(form);
      });
    }
  }
}

// Register the custom element
customElements.define('media-sources-settings', MediaSourcesSettings);

